import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import { type Ref, forwardRef } from "react";
import { cn } from "../../lib/utils";

export const Command = forwardRef<HTMLDivElement, Parameters<typeof CommandPrimitive>[0]>(
  function Command({ className, ...props }, ref) {
    return (
      <CommandPrimitive
        ref={ref}
        className={cn(
          "flex h-full w-full flex-col overflow-hidden rounded-md bg-(--bg-overlay) text-(--text-primary)",
          className,
        )}
        {...props}
      />
    );
  },
);

export const CommandInput = forwardRef<
  HTMLInputElement,
  Parameters<typeof CommandPrimitive.Input>[0]
>(function CommandInput({ className, ...props }, ref: Ref<HTMLInputElement>) {
  return (
    <div className="flex items-center border-b border-(--border-subtle) px-3" cmdk-input-wrapper="">
      <Search size={16} className="mr-2 text-(--text-tertiary)" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          "flex h-10 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-(--text-tertiary) disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
});

export const CommandList = forwardRef<HTMLDivElement, Parameters<typeof CommandPrimitive.List>[0]>(
  function CommandList({ className, ...props }, ref) {
    return (
      <CommandPrimitive.List
        ref={ref}
        className={cn("max-h-80 overflow-y-auto overflow-x-hidden", className)}
        {...props}
      />
    );
  },
);

export const CommandEmpty = forwardRef<
  HTMLDivElement,
  Parameters<typeof CommandPrimitive.Empty>[0]
>(function CommandEmpty(props, ref) {
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className="py-6 text-center text-sm text-(--text-tertiary)"
      {...props}
    />
  );
});

export const CommandGroup = forwardRef<
  HTMLDivElement,
  Parameters<typeof CommandPrimitive.Group>[0]
>(function CommandGroup({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cn(
        "overflow-hidden p-1 text-(--text-primary) [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-(--text-tertiary)",
        className,
      )}
      {...props}
    />
  );
});

export const CommandItem = forwardRef<HTMLDivElement, Parameters<typeof CommandPrimitive.Item>[0]>(
  function CommandItem({ className, ...props }, ref) {
    return (
      <CommandPrimitive.Item
        ref={ref}
        className={cn(
          "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-(--bg-hover) aria-selected:text-(--text-primary) data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);

export const CommandSeparator = forwardRef<
  HTMLDivElement,
  Parameters<typeof CommandPrimitive.Separator>[0]
>(function CommandSeparator({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Separator
      ref={ref}
      className={cn("-mx-1 h-px bg-(--border-subtle)", className)}
      {...props}
    />
  );
});

export const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn("ml-auto text-xs tracking-widest text-(--text-tertiary)", className)}
    {...props}
  />
);
